import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime, Observable, switchMap, filter, tap, map } from 'rxjs';

import { StackingPlanReviewer } from '../../stacking-plan.model';
import { StackingPlanService } from '../../stacking-plan.service';
import { IUser } from '../../models';
import { UTC_EDP_INPUT_FORMAT } from '../../shared/constants/date.const';
import { FieldValidation, FieldValidationsFactory } from '@miq/catalog';

const MIQ_DATE_FORMATS = {
  parse: {
    dateInput: 'MM/DD/YYYY',
  },
  display: {
    dateInput: 'MM/DD/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'sp-add-last-reviewed',
  templateUrl: './add-last-reviewed.component.html',
  styleUrls: ['./add-last-reviewed.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: MIQ_DATE_FORMATS }],
  standalone: false,
})
export class AddLastReviewedComponent implements OnInit {
  // * Enable flag only when a contact from the autocomplete list is selected...
  reviewerChange = false;
  currentReviewer: string;
  reviewedForm: FormGroup;
  cbreEmployees$: Observable<IUser[]>;
  defaultEmployee: IUser[] = [{ id: null, firstName: '', lastName: '', emailAddress: '' }];

  constructor(
    @Inject(MAT_DIALOG_DATA) private reviewer: StackingPlanReviewer,
    private stackingPlanService: StackingPlanService,
    private dialogRef: MatDialogRef<AddLastReviewedComponent>
  ) {}

  ngOnInit() {
    this.initForm();
    this.cbreEmployees$ = this.searchCbreUser();
  }

  private initForm(): void {
    const { reviewedOn, reviewedBy } = this.reviewer || {};
    this.currentReviewer = reviewedBy;
    this.reviewedForm = new FormGroup({
      reviewedOn: new FormControl(reviewedOn, [
        Validators.required,
        FieldValidationsFactory({ type: 'NON_FUTURE_DATE' } as FieldValidation),
      ]),
      reviewedBy: new FormControl(reviewedBy, Validators.required),
    });
  }

  private searchCbreUser(): Observable<IUser[]> {
    const reviewBy = this.reviewedForm.get('reviewedBy');
    return reviewBy.valueChanges.pipe(
      debounceTime(200),
      tap(query => {
        if (typeof query === 'object') {
          this.reviewerChange = false;
          return;
        }
        this.reviewerChange = this.currentReviewer !== query;
      }),
      filter(this.searchCondition),
      switchMap(query => this.stackingPlanService.searchCbreEmployees(query)),
      map(result => (result?.length ? result : this.defaultEmployee))
    );
  }

  private searchCondition(query: string): boolean {
    return typeof query === 'string' && query.length >= 3;
  }

  displayUser(user: IUser | string): string {
    if (!user || typeof user === 'string') {
      return (user as string) || '';
    }
    const { firstName, lastName, emailAddress } = user;
    return `${firstName} ${lastName} (${emailAddress})`;
  }

  saveReviewer(): void {
    const { reviewedBy, reviewedOn } = this.reviewedForm.value;
    const reviewerDate = typeof reviewedOn === 'string' ? reviewedOn : reviewedOn.format(UTC_EDP_INPUT_FORMAT);
    const reviewerFullName =
      typeof reviewedBy === 'object'
        ? `${reviewedBy.firstName} ${reviewedBy.lastName} (${reviewedBy.emailAddress})`
        : reviewedBy;

    this.dialogRef.close({
      reviewedBy: reviewerFullName,
      reviewedOn: reviewerDate,
    });
  }
}
